import { useCallback } from 'react';
import { useOpenLedgerDrawer } from '@features/patient-ledger';

import type { EligibilitySource } from '../types/source';

/**
 * Per-call input for opening the unified Patient Ledger Drawer from an
 * eligibility surface (popover, drawer summary, edit-appointment slide,
 * worklist row).
 *
 * `encounterId` and `appointmentId` are intentionally separate fields —
 * encounters and appointments are distinct resources, and conflating them
 * 404s the encounter-scoped collection endpoint. Pass each in the matching
 * slot.
 *
 * `memberObligationCents` is the BE-computed amount the patient owes.
 * Drives the prefill for the settle sheet's amount field; pass `null` /
 * omit when no amount is known and the sheet will open empty.
 *
 * `source` is the analytics tag carried into Statsig events. Loosely typed
 * (`EligibilitySource | string`) so prototype surfaces can use their own
 * tags without lying through the type system.
 */
export interface EligibilityActionInput {
  patientId?: string;
  patientName?: string;
  encounterId?: string;
  appointmentId?: string;
  memberObligationCents?: number | null;
  source: EligibilitySource | (string & {});
}

export interface EligibilityActions {
  /**
   * Open the unified Patient Ledger Drawer pre-targeting the invoice flow.
   * Lands on the Ledger tab with the Settle sheet pre-opened to method=invoice.
   */
  openInvoice: (input: EligibilityActionInput) => void;
  /**
   * Open the unified Patient Ledger Drawer pre-targeting the
   * external-collection flow. Lands on the Ledger tab with the Settle sheet
   * pre-opened to method=external. In encounter scope the BE call uses
   * `create_charge=true` so the encounter mints a fresh charge_line +
   * applies the payment in one shot — the eligibility badge can then flip
   * `collect → collected`.
   */
  openLogCollection: (input: EligibilityActionInput) => void;
  /**
   * Open the unified Patient Ledger Drawer landing on the Eligibility tab.
   * Used for ambient row clicks / badge clicks where the user wants to
   * inspect coverage rather than take a payment action.
   *
   * `autoOpen.verificationForm` pre-expands the verification log form on
   * mount — used by worklist rows in blocked / attention / expired states.
   */
  openDrawer: (
    input: EligibilityActionInput,
    autoOpen?: { verificationForm?: boolean },
  ) => void;
}

/**
 * Single helper for opening the unified Patient Ledger Drawer from any
 * eligibility trigger. Centralizes the (patientId, encounterId,
 * appointmentId, amount, source) wiring so drift here can't 404 the BE
 * the way it did pre-unification.
 *
 * All three actions land in the same drawer, just with different initial
 * state (tab + sheet). No more separate eligibility detail drawer — its
 * content is now the Eligibility tab in the unified drawer.
 */
export function useEligibilityActions(): EligibilityActions {
  const openLedgerDrawer = useOpenLedgerDrawer();

  const openInvoice = useCallback(
    (input: EligibilityActionInput) => {
      if (!input.patientId) return;
      openLedgerDrawer({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        defaultAmountCents: input.memberObligationCents ?? undefined,
        initialTab: 'ledger',
        initialAction: { kind: 'settle', method: 'invoice' },
        source: input.source,
      });
    },
    [openLedgerDrawer],
  );

  const openLogCollection = useCallback(
    (input: EligibilityActionInput) => {
      if (!input.patientId) return;
      openLedgerDrawer({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        defaultAmountCents: input.memberObligationCents ?? undefined,
        initialTab: 'ledger',
        initialAction: { kind: 'settle', method: 'external' },
        source: input.source,
      });
    },
    [openLedgerDrawer],
  );

  const openDrawer = useCallback(
    (
      input: EligibilityActionInput,
      autoOpen?: { verificationForm?: boolean },
    ) => {
      if (!input.patientId) return;
      openLedgerDrawer({
        patientId: input.patientId,
        patientName: input.patientName ?? null,
        encounterId: input.encounterId,
        defaultAmountCents: input.memberObligationCents ?? undefined,
        initialTab: 'eligibility',
        initialEligibilityForm: autoOpen?.verificationForm
          ? 'verification'
          : undefined,
        source: input.source,
      });
    },
    [openLedgerDrawer],
  );

  return { openInvoice, openLogCollection, openDrawer };
}
